import { useState } from 'react';

import cn from 'classnames';
import { MESSAGE_STATE_ERROR, MESSAGE_STATE_SUCCESS } from 'constants/message-app-state-contants';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { Badge } from 'react-bootstrap';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { ConfirmationModal } from 'components/ConfirmationModal/ConfirmationModal';
import { Button } from 'components/FormComponents';
import { IconButton } from 'components/IconButton/IconButton';
import { Table } from 'components/Table/Table';

import { selectAuthentication } from 'api/authentication-slice';
import { useInviteUserMutation, useResendUserInviteEmailMutation } from 'api/user-api';
import { useDisable2FAMutation } from 'api/user-api';

import { NewUserModal } from '../../components/NewUserModal/NewUserModal';
import { SingleSignOnModal } from '../../components/SingleSignOnModal/SingleSignOnModal';
import styles from './UsersTab.module.scss';

export const UsersTab = ({
  company,
  roles,
  teams,
  getCompanyUsers = () => {},
  messagePopUp = () => {},
  setLoading = () => {},
  deleteUser = () => {},
  updateCompanyOwner = () => {},
  createSSOOrganization = () => {},
  disableSSOOrganization = () => {},
}) => {
  const auth = useSelector(selectAuthentication);

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const [inviteUser] = useInviteUserMutation();
  const [resendUserInviteEmail] = useResendUserInviteEmailMutation();
  const [disable2FA] = useDisable2FAMutation();

  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [showSSOModal, setShowSSOModal] = useState(false);
  const [showDisableMFAModal, setShowDisableMFAModal] = useState(false);

  const [userToSetAsAdmin, setUserToSetAsAdmin] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [didableSSOModal, setDidableSSOModal] = useState(false);

  const fetchUsers = async (params = {}) => {
    setLoading(true);
    const { data } = await getCompanyUsers({ id: company.id, params });
    setPagination(data.result.pagination);
    setUsers(data.result.users);
    setLoading(false);
  };

  const handleNewUser = async userData => {
    setShowNewUserModal(false);
    setLoading(true);
    const { data: response } = await inviteUser(userData);
    if (response.result) {
      setUsers(curr => [...curr, response.result]);
      messagePopUp({
        text: `User ${userData.first_name} was successfully invited`,
        state: MESSAGE_STATE_SUCCESS,
        hide: true,
      });
    } else {
      messagePopUp({
        text: `Email ${userData.email} it's being use by another user`,
        state: MESSAGE_STATE_ERROR,
        hide: true,
      });
    }
    setLoading(false);
  };

  const setCompanyOwner = async () => {
    setLoading(true);
    await updateCompanyOwner({ id: company.id, userId: userToSetAsAdmin.id });
    setUserToSetAsAdmin(false);
    setLoading(false);
  };

  const handleUserDeletion = async () => {
    setLoading(true);
    const { data: result } = await deleteUser(userToDelete.id);
    setUsers(users.filter(u => u.id !== userToDelete.id));
    messagePopUp({
      text: `${userToDelete.name} Deleted. ${result} candidates reassigned`,
      state: MESSAGE_STATE_SUCCESS,
      hide: true,
    });
    setUserToDelete(null);
    setLoading(false);
  };

  const handleDisableMFAModal = async () => {
    setLoading(true);
    await disable2FA(showDisableMFAModal.id);
    setUsers(
      users.map(user =>
        user.id === showDisableMFAModal.id ? { ...user, two_factor_auth_enabled: false } : user,
      ),
    );
    setShowDisableMFAModal(false);
    setLoading(false);
  };

  const handleDisableSSO = async () => {
    setLoading(true);
    await disableSSOOrganization();
    setDidableSSOModal(null);
    setLoading(false);
  };

  const handleResendUserInviteEmail = async user => {
    setLoading(true);
    await resendUserInviteEmail(user.id);
    messagePopUp({
      text: `Invite re-sent for ${user.name}`,
      state: MESSAGE_STATE_SUCCESS,
      hide: true,
    });
    setLoading(false);
  };

  return (
    <div>
      <ConfirmationModal
        visible={!!userToSetAsAdmin}
        title={`Set ${userToSetAsAdmin?.name} as company owner`}
        subtitle={`Are you sure you want to set ${userToSetAsAdmin?.name} as Company owner? This will make them the main user for the company.`}
        onClose={() => {
          setUserToSetAsAdmin(null);
        }}
        onConfirmation={setCompanyOwner}
        buttonTitle="Set as owner"
      />

      <ConfirmationModal
        visible={!!showDisableMFAModal}
        title={`Disable MFA for ${showDisableMFAModal?.name}`}
        subtitle={`Are you sure you want to disable MFA for ${showDisableMFAModal?.name}?`}
        onClose={() => {
          setShowDisableMFAModal(false);
        }}
        onConfirmation={handleDisableMFAModal}
        buttonTitle="Disable MFA"
      />

      <ConfirmationModal
        visible={!!userToDelete}
        title={`Delete ${userToDelete?.name}`}
        subtitle={
          <span>
            Are you sure you want to delete <b>{userToDelete?.name}</b> as user? <br />
            The user won't longer have access to Checkmate. We will also transfer all it's
            candidates to the company administrator. <br />
            <b>This cannot be undone.</b>
          </span>
        }
        onClose={() => {
          setUserToDelete(null);
        }}
        confirmationKey={userToDelete?.email}
        onConfirmation={handleUserDeletion}
        buttonTitle="Delete user"
      />

      <ConfirmationModal
        visible={didableSSOModal}
        title={`Disable Single Sign On`}
        subtitle={
          <span>
            <br></br>
            Are you sure you want to disable this SSO?
          </span>
        }
        onClose={() => {
          setDidableSSOModal(null);
        }}
        onConfirmation={handleDisableSSO}
        buttonTitle="Disable"
      />

      <NewUserModal
        roles={roles}
        teams={teams}
        visible={showNewUserModal}
        onSubmit={handleNewUser}
        onClose={() => setShowNewUserModal(false)}
      />

      <SingleSignOnModal
        company={company}
        visible={showSSOModal}
        setLoading={setLoading}
        onClose={() => setShowSSOModal(false)}
        createSSOOrganization={createSSOOrganization}
      />
      <Table.Context onQueryUpdate={fetchUsers} defaultQuery={{ page: 1, search: '' }}>
        <Table.TableContent>
          <Table.Filters>
            <Table.LeftFilters>
              <Table.Search placeholder="Search for a user..." />
            </Table.LeftFilters>
            <Table.RightFilters>
              <Button
                className="u-margin-right"
                type={company?.sso_organization_id ? 'delete' : 'primary'}
                icon={company?.sso_organization_id ? 'trash-2' : 'cloud'}
                onClick={() =>
                  company?.sso_organization_id ? setDidableSSOModal(true) : setShowSSOModal(true)
                }>
                {company?.sso_organization_id ? 'Disable Single Sign On' : 'Single Sign On'}
              </Button>
              <Button icon="user-plus" onClick={() => setShowNewUserModal(true)}>
                Invite
              </Button>
            </Table.RightFilters>
          </Table.Filters>
          <Table className="u-margin-top--large">
            <Table.Head>
              <Table.Row>
                <Table.Header sortKey="users.first_name">Name</Table.Header>
                <Table.Header sortKey="users.email">Email</Table.Header>
                <Table.Header>Phone number</Table.Header>
                <Table.Header>MFA</Table.Header>
                <Table.Header>Status</Table.Header>
                <Table.Header></Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {users.map(user => (
                <Table.Row key={user.id}>
                  <Table.Col styles={{ 'vertical-align': 'center' }}>
                    <div className={styles.userNameColumn}>
                      <AdminUserToggle
                        company={company}
                        user={user}
                        onClick={setUserToSetAsAdmin}
                      />{' '}
                      {user.name}
                    </div>
                  </Table.Col>
                  <Table.Col>{user.email}</Table.Col>
                  <Table.Col>{user.contact_number}</Table.Col>
                  <Table.Col>
                    {company.creator_id === auth.user_id && user.two_factor_auth_enabled ? (
                      <OverlayTrigger placement="top" overlay={<Tooltip>Disable MFA</Tooltip>}>
                        <Badge
                          className="u-status-completed u-pointer"
                          onClick={() => setShowDisableMFAModal(user)}>
                          Enabled
                        </Badge>
                      </OverlayTrigger>
                    ) : user.two_factor_auth_enabled ? (
                      <Badge className="u-status-completed">Enabled</Badge>
                    ) : (
                      <Badge className="u-status-canceled">Disabled</Badge>
                    )}
                  </Table.Col>
                  <Table.Col>
                    <Badge className={user.verified ? 'u-status-completed' : 'u-status-pending'}>
                      {user.verified ? 'Verified' : 'Pending'}
                    </Badge>
                  </Table.Col>
                  <Table.Col align="right">
                    {!user.verified && (
                      <IconButton
                        icon="send"
                        onClick={() => handleResendUserInviteEmail(user)}
                        tip="Resend verification email"
                      />
                    )}
                    {user.id !== company.creator_id && (
                      <IconButton
                        icon="trash-2"
                        className="u-margin-left--small"
                        onClick={() => setUserToDelete(user)}
                        tip={`Delete ${user.name}`}
                      />
                    )}
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
            <Table.Pagination {...pagination} />
          </Table>
        </Table.TableContent>
      </Table.Context>
    </div>
  );
};

const AdminUserToggle = ({ company, user, onClick = () => {} }) => {
  const isCompanyCreator = company.creator_id === user.id;

  const handleClick = () => {
    if (isCompanyCreator) return;

    onClick(user);
  };

  return (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>{isCompanyCreator ? 'Company owner' : 'Set as company owner'}</Tooltip>}>
      <div>
        <FeatherIcon
          size={17}
          className={cn(styles.starIcon, { [styles.selected]: company.creator_id === user.id })}
          icon="star"
          onClick={handleClick}
        />
      </div>
    </OverlayTrigger>
  );
};
